import { type DateTimeFormatOptions, useFormatter } from 'next-intl'

// Token to DateTimeFormatOptions mapping
const formatMapping: Record<string, DateTimeFormatOptions> = {
  DD: { day: '2-digit' },
  D: { day: 'numeric' },
  MMM: { month: 'short' },
  MMMM: { month: 'long' },
  MM: { month: '2-digit' },
  M: { month: 'numeric' },
  YYYY: { year: 'numeric' },
  YY: { year: '2-digit' },
  HH: { hour: '2-digit', hour12: false },
  H: { hour: 'numeric', hour12: false },
  hh: { hour: '2-digit', hour12: true },
  h: { hour: 'numeric', hour12: true },
  mm: { minute: '2-digit' },
  m: { minute: 'numeric' },
  ss: { second: '2-digit' },
  s: { second: 'numeric' },
  SSS: { fractionalSecondDigits: 3 },
  SS: { fractionalSecondDigits: 2 },
  S: { fractionalSecondDigits: 1 },
  A: { hour12: true },
  a: { hour12: true },
  dddd: { weekday: 'long' },
  ddd: { weekday: 'short' },
  dd: { weekday: 'narrow' },
  z: { timeZoneName: 'short' },
  zz: { timeZoneName: 'long' },
}

// Common format presets for frequent use cases
const commonFormats: Record<string, DateTimeFormatOptions> = {
  'DD MMM, YYYY': {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  },
  'YYYY-MM-DD': {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  },
  'DD/MM/YYYY': {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  },
  'MMM DD, YYYY': {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  },
  'MMMM DD, YYYY': {
    month: 'long',
    day: '2-digit',
    year: 'numeric',
  },
  'HH:mm': {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  },
  'HH:mm:ss': {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  },
  'MMM DD,YYYY hh:mm:ss a': {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  },
  'D MMM, YYYY [at] h:mm:ss A': {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  },
}

// Cache for parsed formats to improve performance
const formatCache: Record<string, DateTimeFormatOptions> = {}

/**
 * Converts a format string to DateTimeFormatOptions
 * @param format - Format string (e.g., "YYYY-MM-DD")
 * @returns DateTimeFormatOptions for Intl.DateTimeFormat
 */
const parseFormat = (format: string): DateTimeFormatOptions => {
  // Return from cache if available
  if (formatCache[format]) {
    return formatCache[format]
  }

  // Return predefined format if available
  if (commonFormats[format]) {
    formatCache[format] = commonFormats[format]
    return commonFormats[format]
  }

  // Parse custom format
  const options: DateTimeFormatOptions = {}

  // Match all possible format tokens
  const tokens = format.match(/[A-Za-z]+/g) || []

  tokens.forEach(token => {
    if (formatMapping[token]) {
      Object.assign(options, formatMapping[token])
    }
  })

  // Cache the result for future use
  formatCache[format] = options
  return options
}

type dateInput = Date | string | number | undefined | null

/**
 * Safely converts any date input to a Date object
 * @param date - Date input (Date object, timestamp, or ISO string)
 * @returns Valid Date object or null if invalid
 */
const toDateObject = (date: dateInput): Date | null => {
  if (!date) return null

  try {
    const dateObj = date instanceof Date ? date : new Date(date)
    // Check if date is valid
    return isNaN(dateObj.getTime()) ? null : dateObj
  } catch (e) {
    console.error('Invalid date:', date)
    return null
  }
}

type DateFormatterOptions = {
  timeZone?: string
  // Additional options can be added in the future
}

const useDateFormatter = () => {
  const formatter = useFormatter()

  /**
   * Formats a date according to the specified format
   * @param date - Date to format
   * @param format - Format string or preset name
   * @param options - Additional options including timeZone
   * @returns Formatted date string or empty string if date is invalid
   */
  const formatDate = (date: dateInput, format: string, options?: DateFormatterOptions): string => {
    const dateObj = toDateObject(date)
    if (!dateObj) return ''

    const formatOptions = parseFormat(format)

    // Use provided timeZone or browser's default
    const timeZone = options?.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone
    const formatterOptions = { ...formatOptions, timeZone }

    return formatter.dateTime(dateObj, formatterOptions)
  }

  return { formatDate }
}

export default useDateFormatter
